import "./App.css";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import Amplify from "aws-amplify";
import config from "./aws-exports";

import Header from "./components/Header";
import Footer from "./components/Footer";
import Routes from "./Routes";
import { ThemeProvider } from "@mui/material/styles";
import { Box, Snackbar, Alert, Button, Typography } from "@mui/material";
import { theme } from "./services/Theme";

Amplify.configure(config);

// No public address by design (appointment-only, by-request shop) — telephone/email/sameAs still help local SEO.
const LOCAL_BUSINESS_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  name: "HaasFab",
  url: "https://haasfab.com",
  email: "business@haasfab.com",
  telephone: "+1-419-806-9442",
  description:
    "Fabrication and finishing services including powder coating, Cerakote application, CNC plasma cutting, welding, custom metal signage, fabrication, 3D printing, and CAD modeling. By appointment only.",
  sameAs: [
    "https://www.facebook.com/HaasFab-100925839159371",
    "https://www.instagram.com/haasfab_llc/",
  ],
};

const GA_MEASUREMENT_ID = "G-RPX05PV7HP";
const GA_CONSENT_KEY = "gaConsentChoice";

const initGoogleAnalytics = () => {
  if (typeof window.gtag === "function") {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };

  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID);

  if (!document.getElementById("ga-gtag-script")) {
    const script = document.createElement("script");
    script.id = "ga-gtag-script";
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);
  }
};

function App() {
  const location = useLocation();
  const [displayLocation, setDisplayLocation] = useState(location);
  const [routeTransitionClass, setRouteTransitionClass] = useState("route-fade-in");
  const [analyticsConsent, setAnalyticsConsent] = useState(null);
  const [showConsentBanner, setShowConsentBanner] = useState(false);
  const [successMessage, setSuccessMessage] = useState(false);
  const openSuccessMessage = () => {
    setSuccessMessage(true);
  };
  const handleMessageClose = () => {
    setSuccessMessage(false);
  };

  const [instaFeed, setInstaFeed] = useState([]);
  const updateInstaFeed = (data) => {
    setInstaFeed(data);
  };

  useEffect(() => {
    const savedConsent = localStorage.getItem(GA_CONSENT_KEY);
    if (savedConsent === "granted" || savedConsent === "denied") {
      setAnalyticsConsent(savedConsent);
      setShowConsentBanner(false);
      return;
    }

    setShowConsentBanner(true);
  }, []);

  useEffect(() => {
    if (analyticsConsent !== "granted") {
      return;
    }

    initGoogleAnalytics();

    window.gtag("config", GA_MEASUREMENT_ID, {
      page_path: `${location.pathname}${location.search}${location.hash}`,
    });
  }, [analyticsConsent, location]);

  useEffect(() => {
    const animatedPaths = ["/", "/gallery", "/about"];
    const navigatingMainPages =
      animatedPaths.includes(location.pathname) &&
      animatedPaths.includes(displayLocation.pathname) &&
      location.pathname !== displayLocation.pathname;

    if (!navigatingMainPages) {
      setDisplayLocation(location);
      setRouteTransitionClass("route-fade-in");
      return;
    }

    setRouteTransitionClass("route-fade-out");

    const timeoutId = window.setTimeout(() => {
      setDisplayLocation(location);
      setRouteTransitionClass("route-fade-in");
    }, 180);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [location, displayLocation.pathname]);

  const handleConsentChoice = (choice) => {
    localStorage.setItem(GA_CONSENT_KEY, choice);
    setAnalyticsConsent(choice);
    setShowConsentBanner(false);
  };

  const openPrivacySettings = () => {
    setShowConsentBanner(true);
  };

  return (
    <ThemeProvider theme={theme}>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(LOCAL_BUSINESS_JSON_LD)}
        </script>
      </Helmet>
      <Box className="App" backgroundColor={theme.palette.background.default}>
        <Header openSuccessMessage={openSuccessMessage} />
        <Box
          className={routeTransitionClass}
          sx={{
            marginTop: {xs: "64px", sm: "94px"},
            display: "flex",
            width: "100%",
            maxWidth: "1200px",
            minHeight: "95vh",
            alignItems: "flex-start",
            justifyContent: "center",
          }}
        >
          <Routes
            location={displayLocation}
            openSuccessMessage={openSuccessMessage}
            updateInstaFeed={updateInstaFeed}
            instaFeed={instaFeed}
          />

        </Box>

        <Footer openPrivacySettings={openPrivacySettings} />

        {showConsentBanner && (
          <Box
            sx={{
              position: "fixed",
              left: { xs: 12, sm: 20 },
              right: { xs: 12, sm: 20 },
              bottom: { xs: 12, sm: 20 },
              zIndex: 1600,
              backgroundColor: "background.paper",
              color: "text.primary",
              borderRadius: 2,
              boxShadow: 6,
              padding: 2,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="body1" sx={{ marginBottom: 1 }}>
              We use Google Analytics to understand website traffic. You can
              accept or decline analytics cookies.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => handleConsentChoice("denied")}
              >
                Decline
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => handleConsentChoice("granted")}
              >
                Accept
              </Button>
            </Box>
          </Box>
        )}

        <Snackbar
          open={successMessage}
          onClose={handleMessageClose}
          autoHideDuration={6000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={handleMessageClose} severity="success">
            Contact Form Sent! We'll get back to you ASAP
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}

export default App;
